import _ from 'lodash'
import type {CreateExtArgs} from '@wix/document-manager-core'
import type {CompRef} from '@wix/document-services-types'
import {ReportableError} from '@wix/document-manager-utils'
import {getComponentType} from '../../../../utils/dalUtils'

const COMPONENT_TYPES = {
    BOX_SLIDE_SHOW: 'wysiwyg.viewer.components.BoxSlideShow',
    BOX_SLIDE_SHOW_SLIDE: 'wysiwyg.viewer.components.BoxSlideShowSlide',
    STATE_BOX: 'wysiwyg.viewer.components.StateBox',
    STATE_BOX_FORM_STATE: 'wysiwyg.viewer.components.StateBoxFormState',
    STATE_BOX_STATE: 'wysiwyg.viewer.components.StateBoxState',
    STATE_STRIP: 'wysiwyg.viewer.components.StateStrip',
    STATE_STRIP_STATE: 'wysiwyg.viewer.components.StateStripState',
    STRIP_CONTAINER_SLIDE_SHOW: 'wysiwyg.viewer.components.StripContainerSlideShow',
    STRIP_CONTAINER_SLIDE_SHOW_SLIDE: 'wysiwyg.viewer.components.StripContainerSlideShowSlide',
    REPEATER: 'wysiwyg.viewer.components.Repeater'
} as const

const CANNOT_DELETE_LAST_SLIDE_ERROR = 'CannotDeleteLastSlide'

const childTypeByContainerType: Record<string, string | string[]> = {
    [COMPONENT_TYPES.BOX_SLIDE_SHOW]: COMPONENT_TYPES.BOX_SLIDE_SHOW_SLIDE,
    [COMPONENT_TYPES.STRIP_CONTAINER_SLIDE_SHOW]: COMPONENT_TYPES.STRIP_CONTAINER_SLIDE_SHOW_SLIDE,
    [COMPONENT_TYPES.STATE_BOX]: [
        COMPONENT_TYPES.STATE_BOX_STATE,
        COMPONENT_TYPES.STATE_BOX_FORM_STATE,
        COMPONENT_TYPES.REPEATER
    ],
    [COMPONENT_TYPES.STATE_STRIP]: COMPONENT_TYPES.STATE_STRIP_STATE
}

export const SLIDE_COMPONENT_TYPES = _.flatten(_.values(childTypeByContainerType))

const isSlideComponent = (componentType: string) => _.includes(SLIDE_COMPONENT_TYPES, componentType)

const getSlidesFromChildrenByStructure = (children: {componentType: string}[]) =>
    _.filter(children, ({componentType}) => isSlideComponent(componentType))

const LEGAL_SLIDE_PARENT: Record<string, string> = {
    [COMPONENT_TYPES.BOX_SLIDE_SHOW_SLIDE]: COMPONENT_TYPES.BOX_SLIDE_SHOW,
    [COMPONENT_TYPES.STATE_BOX_STATE]: COMPONENT_TYPES.STATE_BOX,
    [COMPONENT_TYPES.STATE_BOX_FORM_STATE]: COMPONENT_TYPES.STATE_BOX,
    [COMPONENT_TYPES.REPEATER]: COMPONENT_TYPES.STATE_BOX,
    [COMPONENT_TYPES.STATE_STRIP_STATE]: COMPONENT_TYPES.STATE_STRIP,
    [COMPONENT_TYPES.STRIP_CONTAINER_SLIDE_SHOW_SLIDE]: COMPONENT_TYPES.STRIP_CONTAINER_SLIDE_SHOW
}

const isParentIllegal = (createExtArgs: CreateExtArgs, deletedCompPointer: CompRef, slideType: string): boolean => {
    const {dal, pointers} = createExtArgs
    const slideParent = pointers.structure.getParent(deletedCompPointer)

    if (!slideParent) {
        return true
    }

    const slideParentType = getComponentType(dal, slideParent)
    return slideParentType !== LEGAL_SLIDE_PARENT[slideType]
}

export const verifySlideShowStructureOnDelete = (
    createExtArgs: CreateExtArgs,
    compPtr: CompRef,
    compType: string,
    deletingParent: boolean
): void => {
    const {dal, pointers} = createExtArgs
    if (deletingParent || isParentIllegal(createExtArgs, compPtr, compType)) {
        return
    }

    const slideParent = pointers.structure.getParent(compPtr)
    if (!slideParent) {
        return
    }

    const childrenPointers = pointers.structure.getChildren(slideParent)
    const childrenTypes = _.map(childrenPointers, child => ({componentType: getComponentType(dal, child)}))
    const slides = getSlidesFromChildrenByStructure(childrenTypes)
    if (slides.length === 1) {
        throw new ReportableError({
            errorType: CANNOT_DELETE_LAST_SLIDE_ERROR,
            message: `Cannot delete the last slide from ${slideParent.id}`
        })
    }
}
